import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList,
} from "@radix-ui/react-navigation-menu";
import { useState, useEffect } from "react";
import { Menu, User, LogOut } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { API_ENDPOINTS } from "@/constants/constants";

export default function EtudiantHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [user, setUser] = useState<{ name?: string; role?: string } | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Fonction pour déterminer si un lien est actif
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // Fonction pour obtenir la classe CSS du lien selon son état actif ou non
  const getLinkClassName = (path: string) => {
    return isActive(path)
      ? "bg-white text-green-600 border-2 border-green-600 px-3 py-2 rounded-sm text-xs font-semibold mobile:text-center tablet:text-center flex items-center justify-center md:justify-start"
      : "bg-orange-500 text-white px-3 py-2 rounded-sm text-xs font-semibold mobile:text-center tablet:text-center flex items-center justify-center md:justify-start";
  };

  useEffect(() => {
    // Check if user is authenticated
    const accessToken = localStorage.getItem("accessToken");
    const userStr = localStorage.getItem("user");

    if (accessToken && userStr) {
      try {
        const userData = JSON.parse(userStr);
        setUser(userData);
      } catch {
        setUser(null);
      }
    } else {
      setUser(null);
    }
  }, []);

  // Fermer le menu du profil lorsqu'on clique ailleurs sur la page
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (profileMenuOpen && !target.closest(".profile-menu-container")) {
        setProfileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profileMenuOpen]);

  const handleLogout = async () => {
    try {
      // Récupérer le token d'accès
      const accessToken = localStorage.getItem("accessToken");

      // Appeler l'API de déconnexion
      await fetch(API_ENDPOINTS.AUTH.LOGOUT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      // Nettoyer le localStorage
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      localStorage.removeItem("previousPath");

      // Mettre à jour l'état local
      setUser(null);
      setProfileMenuOpen(false);

      // Rediriger vers la page de connexion
      navigate("/login");
    } catch (error) {
      console.error("Erreur lors de la déconnexion:", error);
      // En cas d'erreur, on redirige quand même vers la page de connexion
      navigate("/login");
    }
  };

  return (
    <div className="bg-[#fefaf6] w-full font-myriad">
      <header className="py-3 px-5 container mx-auto relative">
        {/* Première ligne : Logo et Image */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-3 mobile:mb-5 tablet:mb-5">
          <div className="flex flex-col justify-center items-center mb-2 md:mb-0 mobile:w-full tablet:w-full">
            <div className="flex justify-center md:justify-start mobile:justify-center tablet:justify-center w-full">
              <img
                src="/images/logogp.png"
                alt="Guide Pro"
                className="h-10 mobile:h-6 tablet:h-7 w-auto"
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Image du ministère */}
            <div className="flex flex-col items-center mobile:w-full tablet:w-full mobile:mt-3 tablet:mt-3">
              <img
                src="/images/armoirie-transp.png"
                alt="armoirie"
                className="w-[55px] h-auto mb-2"
              />
              <p className="text-center leading-[1.1] text-[11px]">
                MINISTERE DE LA FONCTION <br />
                PUBLIQUE ET DE LA MODERNISATION <br />
                DE L'ADMINISTRATION
              </p>
            </div>
          </div>
        </div>

        {/* Bouton menu burger mobile et tablette uniquement */}
        <button
          className="md:hidden mobile:block tablet:block absolute top-5 right-5 p-2 bg-orange-500 text-white rounded"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <Menu size={20} />
        </button>

        {/* Deuxième ligne : Menu et Bouton */}
        <div
          className={`flex flex-col md:flex-row justify-between items-center ${
            menuOpen ? "flex" : "hidden md:flex"
          }`}
        >
          {/* Menu de navigation */}
          <div className="flex space-x-2 mb-3 md:mb-0 w-full mobile:w-full tablet:w-full md:w-auto desktop:w-auto mobile:mb-5 tablet:mb-5">
            <NavigationMenu className="w-full md:w-auto">
              <NavigationMenuList className="flex flex-wrap justify-center md:justify-start gap-2 mobile:flex-col tablet:flex-col mobile:space-y-2 tablet:space-y-2 mobile:w-full tablet:w-full">
                <NavigationMenuItem className="mobile:w-full tablet:w-full">
                  <Link
                    to="/parrain/dashboard"
                    className={getLinkClassName("/parrain/dashboard")}
                  >
                    TABLEAU DE BORD
                  </Link>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* Bouton Profil */}
          <div className="profile-menu-container relative mobile:self-center tablet:self-center md:self-end md:mr-4 desktop:mr-8 mt-2 md:mt-0">
            <button
              onClick={() => setProfileMenuOpen(!profileMenuOpen)}
              className="flex items-center justify-center bg-green-600 hover:bg-green-700 text-white p-2 rounded-full shadow-md"
              aria-label="Profil utilisateur"
            >
              <Avatar className="h-8 w-8">
                <AvatarImage
                  src="/images/avatar-placeholder.png"
                  alt="Photo de profil"
                />
                <AvatarFallback className="bg-green-700 text-white">
                  <User size={16} />
                </AvatarFallback>
              </Avatar>
            </button>

            {profileMenuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                <div className="py-1">
                  {user?.name && (
                    <div className="block px-4 py-2 text-sm font-semibold text-gray-900 border-b border-gray-200">
                      {user.name}
                    </div>
                  )}
                  <Link
                    to="/professeur/profil"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 items-center gap-2"
                  >
                    <User size={16} />
                    Profil
                  </Link>
                  <Link
                    to="#"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 items-center gap-2"
                    onClick={(e) => {
                      e.preventDefault();
                      handleLogout();
                    }}
                  >
                    <LogOut size={16} />
                    Se déconnecter
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
    </div>
  );
}
